import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

const N8N_AFILIADO_LOGIN_URL = 'https://n8n.goautomat.com/webhook/afiliado-login';
const N8N_AFILIADO_DASHBOARD_URL = 'https://n8n.goautomat.com/webhook/afiliado-dashboard';
const N8N_AFILIADO_LOGOUT_URL = 'https://n8n.goautomat.com/webhook/afiliado-logout';
const N8N_PIX_OTP_SOLICITAR_URL = 'https://n8n.goautomat.com/webhook/afiliado-pix-otp-solicitar';
const N8N_PIX_OTP_CONFIRMAR_URL = 'https://n8n.goautomat.com/webhook/afiliado-pix-otp-confirmar';
const N8N_PIX_CANCELAR_PENDENTE_URL = 'https://n8n.goautomat.com/webhook/afiliado-pix-cancelar-pendente';
const AFILIADO_TOKEN_KEY = 'inspira_afiliado_token';
const AFILIADO_NAO_ENCONTRADO_MSG = 'Desculpe, não conseguimos encontrar um usuário com este endereço de e-mail ligado à esse cupom.\n\nEm caso de dúvidas, contate-nos em contato@inspiramusicas.com.br';
const PIX_TIPOS = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'TELEFONE', label: 'Telefone' },
  { value: 'ALEATORIA', label: 'Chave aleatória' },
];
const GRACE_PERIOD_HOURS = 48;

type Venda = {
  id: string;
  comprador_nome: string;
  pedido_ref_id: string;
  cupom_codigo: string;
  valor_comissao: number;
  status: 'pendente' | 'pago' | 'falha' | string;
  compra_em: string | null;
  disponivel_em: string | null;
  pago_em: string | null;
};

type DashboardData = {
  afiliado: {
    id: string;
    nome: string;
    email: string;
    whatsapp: string;
    pix_tipo: string;
    pix_chave: string;
    pix_tipo_pendente?: string | null;
    pix_chave_pendente?: string | null;
    pix_pendente_em?: string | null;
    cupons: string[];
  };
  resumo: {
    total_vendas: number;
    total_comissao: number;
    total_pendente: number;
    total_pago: number;
  };
  vendas: Venda[];
};

function formatCurrency(v: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
}

function formatDateTime(value: string | null) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function maskPixKey(pix: string) {
  const raw = String(pix || '');
  if (raw.length <= 6) return raw;
  return `${raw.slice(0, 3)}••••••${raw.slice(-3)}`;
}

function maskWhatsApp(phone: string) {
  const raw = String(phone || '').replace(/\D/g, '');
  if (raw.length < 8) return phone || '';
  return `(${raw.slice(-11, -9) || '••'}) •••••-${raw.slice(-4)}`;
}

function calcGraceEnd(pendenteEm: string | null | undefined): Date | null {
  if (!pendenteEm) return null;
  const d = new Date(pendenteEm);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(d.getHours() + GRACE_PERIOD_HOURS);
  return d;
}

function formatTimeUntil(end: Date): string {
  const diffMs = end.getTime() - Date.now();
  if (diffMs <= 0) return 'em instantes';
  const totalMin = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const minutes = totalMin % 60;
  if (days > 0) return `em ${days}d${hours > 0 ? ` ${hours}h` : ''}`;
  if (hours > 0) return `em ${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
  return `em ${minutes}min`;
}

const WHATSAPP_BOT_NUMBER = '5511926797492';
const SITE_BASE_URL = 'https://www.inspiramusicas.com.br';

function buildKitLinks(cupom: string) {
  const code = cupom.toUpperCase();
  const waText = `Oi! Quero fazer um pedido com o cupom ${code}`;
  return {
    code,
    whatsapp: `https://wa.me/${WHATSAPP_BOT_NUMBER}?text=${encodeURIComponent(waText)}`,
    site: `${SITE_BASE_URL}/criar?ref=${code}`,
    pretty: {
      whatsapp: `wa.me/...?text=cupom ${code}`,
      site: `inspiramusicas.com.br/criar?ref=${code}`,
    },
  };
}

function buildKitCopies(cupom: string) {
  const code = cupom.toUpperCase();
  const url = `${SITE_BASE_URL}/criar?ref=${code}`;
  return {
    detalhado: `Olha que ideia diferente: música personalizada, feita do zero com a história e o jeito da pessoa que você quer homenagear.\n\nEu indico a Inspira Músicas — eles entregam em até 30 minutos por e-mail.\n\nUse meu cupom *${code}* e ganhe desconto:\n${url}`,
    instagram: `🎵 Música personalizada em 30 minutos\n🎁 Cupom de desconto: ${code}\n👉 ${url.replace('https://', '')}`,
    curto: `Música personalizada com a história da pessoa especial 🎵\nEntrega em 30 min · Cupom ${code}:\n${url}`,
  };
}

function ShareLinkCard({ icon, title, subtitle, url, prettyUrl, copied, onCopy }: {
  icon: string;
  title: string;
  subtitle: string;
  url: string;
  prettyUrl: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div style={{
      padding: '16px 18px',
      borderRadius: 14,
      border: '1px solid var(--border-soft)',
      background: 'var(--cream)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 22, lineHeight: 1.1 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--heading-ink)', letterSpacing: '-0.01em' }}>{title}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{subtitle}</p>
        </div>
      </div>
      <p style={{
        margin: 0,
        padding: '8px 12px',
        background: 'var(--gold-pale)',
        border: '1px dashed var(--gold-border)',
        borderRadius: 10,
        fontSize: 12,
        color: 'var(--text-body)',
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {prettyUrl}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onCopy}
          style={{
            flex: 1,
            padding: '10px 14px',
            border: 'none',
            borderRadius: 100,
            background: copied ? '#21824d' : 'var(--gold)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            transition: 'background 0.2s',
            boxShadow: copied ? 'none' : '0 2px 12px rgba(255,145,77,0.30)',
          }}
        >
          {copied ? '✓ Copiado' : 'Copiar link'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '10px 16px',
            border: '1.5px solid var(--border-soft)',
            borderRadius: 100,
            background: 'transparent',
            color: 'var(--text-strong)',
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
            fontFamily: "'DM Sans', sans-serif",
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Abrir ↗
        </a>
      </div>
    </div>
  );
}

function ShareCopyCard({ label, text, copied, onCopy }: {
  label: string;
  text: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div style={{
      padding: '14px 16px',
      borderRadius: 12,
      border: '1px solid var(--border-soft)',
      background: 'var(--cream)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </p>
        <button
          type="button"
          onClick={onCopy}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderRadius: 100,
            background: copied ? '#21824d' : 'var(--gold)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            flexShrink: 0,
            transition: 'background 0.2s',
          }}
        >
          {copied ? '✓ Copiado' : 'Copiar texto'}
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: "'DM Sans', sans-serif" }}>
        {text}
      </p>
    </div>
  );
}

function ShareKit({ cupom }: { cupom: string }) {
  const [copiedKey, setCopiedKey] = useState('');
  if (!cupom) return null;

  const links = buildKitLinks(cupom);
  const copies = buildKitCopies(cupom);

  function handleCopy(key: string, value: string) {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? '' : k)), 2000);
    }).catch(() => {
      // ignora falha de clipboard (browsers antigos)
    });
  }

  return (
    <div id="kit-divulgacao" className="glass rounded-3xl mb-6" style={{
      background: 'var(--surface-white)',
      border: '1px solid var(--border-soft)',
      padding: '24px 26px',
      scrollMarginTop: 100,
    }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{
            display: 'inline-flex',
            padding: '3px 10px',
            borderRadius: 999,
            background: 'var(--badge-bg)',
            color: 'var(--gold)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}>
            Para divulgar
          </span>
        </div>
        <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
          Kit de divulgação
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Compartilhe seus links ou use os textos prontos. Toda venda feita com o cupom <strong style={{ color: 'var(--amber-deep)', letterSpacing: '0.03em' }}>{links.code}</strong> entra no seu painel.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 22 }}>
        <ShareLinkCard
          icon="💬"
          title="Link para WhatsApp"
          subtitle="Abre conversa com a Inspira já com seu cupom incluído na mensagem"
          url={links.whatsapp}
          prettyUrl={links.pretty.whatsapp}
          copied={copiedKey === 'wa-link'}
          onCopy={() => handleCopy('wa-link', links.whatsapp)}
        />
        <ShareLinkCard
          icon="🌐"
          title="Link para o site"
          subtitle="Cupom é aplicado automaticamente no checkout do wizard"
          url={links.site}
          prettyUrl={links.pretty.site}
          copied={copiedKey === 'site-link'}
          onCopy={() => handleCopy('site-link', links.site)}
        />
      </div>

      <div>
        <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Textos prontos para postar
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ShareCopyCard
            label="WhatsApp / Stories"
            text={copies.detalhado}
            copied={copiedKey === 'copy-detalhado'}
            onCopy={() => handleCopy('copy-detalhado', copies.detalhado)}
          />
          <ShareCopyCard
            label="Instagram (legenda ou bio)"
            text={copies.instagram}
            copied={copiedKey === 'copy-insta'}
            onCopy={() => handleCopy('copy-insta', copies.instagram)}
          />
          <ShareCopyCard
            label="Versão curta"
            text={copies.curto}
            copied={copiedKey === 'copy-curto'}
            onCopy={() => handleCopy('copy-curto', copies.curto)}
          />
        </div>
      </div>
    </div>
  );
}

function DadosPagamentoCard({
  afiliado,
  onEdit,
  onCancelPending,
  cancelLoading,
  cancelError,
}: {
  afiliado: DashboardData['afiliado'];
  onEdit: () => void;
  onCancelPending: () => void;
  cancelLoading: boolean;
  cancelError: string;
}) {
  const pendingEnd = calcGraceEnd(afiliado.pix_pendente_em);
  const hasPending = !!(afiliado.pix_chave_pendente && pendingEnd);

  return (
    <div className="glass rounded-3xl mb-6" style={{
      background: 'var(--surface-white)',
      border: '1px solid var(--border-soft)',
      padding: '24px 26px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
        <div style={{ minWidth: 0 }}>
          <span style={{
            display: 'inline-flex',
            padding: '3px 10px',
            borderRadius: 999,
            background: 'var(--badge-bg)',
            color: 'var(--gold)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 8,
          }}>
            Onde você recebe
          </span>
          <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
            Dados de pagamento
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
            Chave PIX <strong style={{ color: 'var(--text-strong)' }}>{afiliado.pix_tipo || '—'}</strong>: <strong style={{ color: 'var(--text-strong)' }}>{maskPixKey(afiliado.pix_chave)}</strong>
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          style={{
            padding: '10px 20px',
            borderRadius: 100,
            border: '1.5px solid var(--border-soft)',
            background: 'transparent',
            color: 'var(--text-strong)',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            transition: 'border-color 0.2s, color 0.2s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-soft)'; e.currentTarget.style.color = 'var(--text-strong)'; }}
        >
          🔒 Editar chave
        </button>
      </div>

      {hasPending && pendingEnd && (
        <div style={{
          marginTop: 16,
          padding: '14px 16px',
          borderRadius: 14,
          border: '1px solid rgba(212, 154, 67, 0.40)',
          background: 'rgba(212, 154, 67, 0.10)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1.2 }}>⏳</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--amber-deep)' }}>
                Mudança em análise
              </p>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5 }}>
                Nova chave <strong>{afiliado.pix_tipo_pendente || '—'}</strong> ({maskPixKey(afiliado.pix_chave_pendente || '')}) entra em vigor {formatTimeUntil(pendingEnd)}. Se não foi você, cancele agora ou responda <strong>CANCELAR</strong> no WhatsApp.
              </p>
            </div>
          </div>
          {cancelError && (
            <p style={{ margin: 0, fontSize: 12, color: '#a53125', fontFamily: "'DM Sans', sans-serif" }}>
              {cancelError}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancelPending}
              disabled={cancelLoading}
              style={{
                padding: '8px 16px',
                borderRadius: 100,
                border: 'none',
                background: cancelLoading ? 'var(--border-fade)' : '#a53125',
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                cursor: cancelLoading ? 'default' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: '0.02em',
              }}
            >
              {cancelLoading ? 'Cancelando...' : 'Cancelar mudança'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const modalSecondaryBtn: React.CSSProperties = {
  padding: '12px 22px',
  borderRadius: 100,
  border: '1.5px solid var(--border-soft)',
  background: 'transparent',
  color: 'var(--text-strong)',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: "'DM Sans', sans-serif",
};

function modalPrimaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '12px 22px',
    borderRadius: 100,
    border: 'none',
    background: disabled ? 'var(--earth-light)' : 'var(--gold)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    boxShadow: disabled ? 'none' : '0 4px 20px rgba(255,145,77,0.35)',
  };
}

type ModalStep = 'form' | 'otp' | 'success';

function EditarPixModal({
  open,
  onClose,
  token,
  whatsappFallbackMascarado,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  token: string;
  whatsappFallbackMascarado: string;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<ModalStep>('form');
  const [pixTipo, setPixTipo] = useState('CPF');
  const [pixChave, setPixChave] = useState('');
  const [otpId, setOtpId] = useState('');
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStep('form');
      setPixTipo('CPF');
      setPixChave('');
      setOtpId('');
      setOtp('');
      setMaskedPhone('');
      setError('');
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  async function handleSendCode() {
    setError('');
    if (!pixChave.trim()) {
      setError('Informe a nova chave PIX.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(N8N_PIX_OTP_SOLICITAR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, pix_tipo: pixTipo, pix_chave: pixChave.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.erro || 'Não foi possível enviar o código.');
      setOtpId(String(data.otp_id || ''));
      setMaskedPhone(String(data.whatsapp_mascarado || ''));
      setStep('otp');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar código.');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setError('');
    if (otp.length !== 6) {
      setError('Digite o código de 6 dígitos.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(N8N_PIX_OTP_CONFIRMAR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, otp_id: otpId, codigo: otp, pix_tipo: pixTipo, pix_chave: pixChave.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.erro || 'Código inválido ou expirado.');
      setStep('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao confirmar.');
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (step === 'success') onSuccess();
    onClose();
  }

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 200,
      }}
    >
      <div
        className="glass"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-white)',
          border: '1px solid var(--border-soft)',
          borderRadius: 24,
          padding: '28px',
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 24px 80px rgba(0,0,0,0.40)',
        }}
      >
        {step === 'form' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 10, lineHeight: 1 }}>🔒</div>
              <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
                Mudar chave PIX
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Para sua segurança, vamos enviar um código no WhatsApp cadastrado antes de salvar.
              </p>
            </div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', display: 'block', marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>
              Tipo da chave
              <select
                value={pixTipo}
                onChange={(e) => setPixTipo(e.target.value)}
                style={{ ...inputStyle, marginTop: 6, cursor: 'pointer' }}
              >
                {PIX_TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', display: 'block', marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>
              Nova chave PIX
              <input
                type="text"
                value={pixChave}
                onChange={(e) => setPixChave(e.target.value)}
                placeholder="Cole ou digite a chave"
                style={{ ...inputStyle, marginTop: 6 }}
                autoFocus
              />
            </label>
            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', color: '#a53125', fontSize: 13, marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" onClick={handleClose} style={modalSecondaryBtn}>Cancelar</button>
              <button type="button" onClick={handleSendCode} disabled={loading} style={modalPrimaryBtnStyle(loading)}>
                {loading ? 'Enviando...' : 'Enviar código →'}
              </button>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 10, lineHeight: 1 }}>📱</div>
              <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
                Confirme com o código
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Enviamos um código de 6 dígitos no WhatsApp <strong style={{ color: 'var(--text-strong)' }}>{maskedPhone || whatsappFallbackMascarado}</strong>. Expira em 10 minutos.
              </p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="••••••"
              style={{
                ...inputStyle,
                fontSize: 26,
                textAlign: 'center',
                letterSpacing: '0.5em',
                fontFamily: 'monospace',
                marginBottom: 14,
                fontWeight: 700,
                paddingRight: 0,
              }}
              autoFocus
            />
            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', color: '#a53125', fontSize: 13, marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
                {error}
              </div>
            )}
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(212, 154, 67, 0.10)', border: '1px solid rgba(212, 154, 67, 0.30)', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>
              ⏳ Por segurança, a nova chave entra em vigor <strong>48 horas</strong> após a confirmação. Você receberá aviso no WhatsApp e e-mail e poderá cancelar se não foi você.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setStep('form')} style={modalSecondaryBtn}>← Voltar</button>
              <button type="button" onClick={handleConfirm} disabled={loading || otp.length !== 6} style={modalPrimaryBtnStyle(loading || otp.length !== 6)}>
                {loading ? 'Confirmando...' : 'Confirmar mudança'}
              </button>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <div style={{ fontSize: 56, marginBottom: 12, lineHeight: 1 }}>✅</div>
              <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
                Mudança solicitada
              </h2>
              <p style={{ margin: '8px auto 0', maxWidth: 360, fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.55, fontFamily: "'DM Sans', sans-serif" }}>
                Sua nova chave PIX será ativada em 48 horas. Se não foi você, cancele no painel ou responda <strong>CANCELAR</strong> no WhatsApp.
              </p>
            </div>
            <button type="button" onClick={handleClose} style={{ ...modalPrimaryBtnStyle(false), width: '100%' }}>
              Entendi
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function OnboardingStep({ number, icon, title, desc }: {
  number: string;
  icon: string;
  title: string;
  desc: string;
}) {
  return (
    <div style={{
      padding: '18px 16px',
      borderRadius: 14,
      border: '1.5px dashed var(--border-soft)',
      background: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: 'var(--gold-pale)',
          color: 'var(--amber-deep)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 12,
          flexShrink: 0,
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: 0,
        }}>
          {number}
        </span>
        <span style={{ fontSize: 22, lineHeight: 1 }}>{icon}</span>
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--heading-ink)', letterSpacing: '-0.01em', fontFamily: "'DM Sans', sans-serif" }}>{title}</p>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>{desc}</p>
      </div>
    </div>
  );
}

function VendasEmptyState({ cupom }: { cupom: string }) {
  const code = (cupom || '').toUpperCase();
  function scrollToKit() {
    const el = document.getElementById('kit-divulgacao');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return (
    <div style={{ padding: '32px 24px' }}>
      <div style={{ textAlign: 'center', padding: '4px 0 22px', maxWidth: 460, margin: '0 auto' }}>
        <div style={{ fontSize: 40, marginBottom: 10, lineHeight: 1 }}>🎁</div>
        <h3 style={{
          margin: 0,
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: '-0.02em',
          color: 'var(--heading-ink)',
        }}>
          Sua primeira venda está a um link de distância
        </h3>
        <p style={{
          margin: '6px 0 0',
          fontSize: 13.5,
          color: 'var(--text-muted)',
          lineHeight: 1.55,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {code
            ? <>Cada compra feita com o cupom <strong style={{ color: 'var(--amber-deep)', letterSpacing: '0.03em' }}>{code}</strong> aparece aqui automaticamente.</>
            : <>Quando uma compra for feita com seu cupom, ela aparece aqui automaticamente.</>}
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}>
        <OnboardingStep
          number="1"
          icon="📤"
          title="Compartilhe seu link"
          desc="Use o kit de divulgação acima e poste no seu Instagram, WhatsApp ou Stories."
        />
        <OnboardingStep
          number="2"
          icon="📊"
          title="Acompanhe vendas aqui"
          desc="Cada compra feita com seu cupom aparece nesta tela com o valor da comissão."
        />
        <OnboardingStep
          number="3"
          icon="💸"
          title="Receba via PIX"
          desc="Comissões disponíveis são pagas via PIX para a chave cadastrada no seu perfil."
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
        <button
          type="button"
          onClick={scrollToKit}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: 100,
            background: 'var(--gold)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: '0 4px 20px rgba(255,145,77,0.32)',
            transition: 'background 0.2s',
          }}
        >
          Abrir kit de divulgação ↑
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    pendente: { label: 'A pagar', bg: 'rgba(212, 154, 67, 0.18)', color: 'var(--amber-deep)' },
    pago:     { label: 'Pago',    bg: 'rgba(50, 160, 95, 0.18)',  color: '#21824d' },
    falha:    { label: 'Falha',   bg: 'rgba(192, 56, 43, 0.16)',  color: '#a53125' },
  };
  const info = map[(status || '').toLowerCase()] || { label: status || 'N/A', bg: 'rgba(125,125,125,0.16)', color: 'var(--text-muted)' };
  return (
    <span style={{ display: 'inline-flex', padding: '5px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", background: info.bg, color: info.color, letterSpacing: '0.03em' }}>
      {info.label}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  marginTop: 6,
  display: 'block',
  width: '100%',
  padding: '12px 14px',
  borderRadius: 12,
  border: '1.5px solid var(--border-soft)',
  background: 'var(--cream)',
  color: 'var(--text-body)',
  outline: 'none',
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  boxSizing: 'border-box',
};

export default function AfiliadoPortalPage() {
  const isDark = useDarkMode();
  const [email, setEmail] = useState('');
  const [cupom, setCupom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [cancelPendingLoading, setCancelPendingLoading] = useState(false);
  const [cancelPendingError, setCancelPendingError] = useState('');

  const cuponsStr = useMemo(() => (dashboard?.afiliado.cupons || []).join(' · '), [dashboard]);

  async function carregarDashboard(token: string) {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(N8N_AFILIADO_DASHBOARD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.erro || 'Não foi possível carregar seu painel.');
      setDashboard(data.data as DashboardData);
    } catch (err: unknown) {
      localStorage.removeItem(AFILIADO_TOKEN_KEY);
      setDashboard(null);
      setError(err instanceof Error ? err.message : 'Falha ao carregar o painel.');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!email.trim()) return setError('Informe seu e-mail.');
    if (!cupom.trim()) return setError('Informe seu cupom.');
    setLoading(true);
    try {
      const res = await fetch(N8N_AFILIADO_LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), cupom: cupom.trim().toUpperCase() }),
      });
      let data: { ok?: boolean; token?: string; erro?: string } | null = null;
      try {
        data = await res.json();
      } catch {
        // resposta vazia ou não-JSON: tratamos como afiliado não encontrado
      }
      if (!res.ok || !data || !data.ok) throw new Error(AFILIADO_NAO_ENCONTRADO_MSG);
      const token = String(data.token || '');
      if (!token) throw new Error(AFILIADO_NAO_ENCONTRADO_MSG);
      localStorage.setItem(AFILIADO_TOKEN_KEY, token);
      await carregarDashboard(token);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : AFILIADO_NAO_ENCONTRADO_MSG);
      setLoading(false);
    }
  }

  async function handleCancelPending() {
    const token = localStorage.getItem(AFILIADO_TOKEN_KEY) || '';
    if (!token) return;
    setCancelPendingLoading(true);
    setCancelPendingError('');
    try {
      const res = await fetch(N8N_PIX_CANCELAR_PENDENTE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.erro || 'Não foi possível cancelar.');
      await carregarDashboard(token);
    } catch (err: unknown) {
      setCancelPendingError(err instanceof Error ? err.message : 'Erro ao cancelar.');
    } finally {
      setCancelPendingLoading(false);
    }
  }

  function handlePixModalSuccess() {
    const token = localStorage.getItem(AFILIADO_TOKEN_KEY) || '';
    if (token) carregarDashboard(token);
  }

  async function handleLogout() {
    const token = localStorage.getItem(AFILIADO_TOKEN_KEY) || '';
    localStorage.removeItem(AFILIADO_TOKEN_KEY);
    setDashboard(null);
    setEmail('');
    setCupom('');
    setError('');
    if (!token) return;
    try {
      await fetch(N8N_AFILIADO_LOGOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } catch {
      // logout local já concluído
    }
  }

  useEffect(() => {
    const token = localStorage.getItem(AFILIADO_TOKEN_KEY);
    if (token) carregarDashboard(token);
  }, []);

  return (
    <>
      <SEOMeta
        title="Portal do Afiliado | Inspira Músicas"
        description="Acompanhe suas vendas, comissões e datas de pagamento no Portal do Afiliado da Inspira Músicas."
        path="/afiliados/portal"
        noIndex
      />

      <div className="portal-shell" style={{ minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
        {/* Header */}
        <header
          className="glass"
          style={{
            background: isDark ? 'rgba(10,5,20,0.65)' : 'rgba(255,250,243,0.75)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,170,110,0.18)'}`,
            position: 'sticky',
            top: 0,
            zIndex: 50,
            boxShadow: isDark ? '0 1px 20px rgba(0,0,0,0.40)' : '0 1px 16px rgba(255,145,77,0.08)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
            <Link to="/afiliados">
              <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 52, objectFit: 'contain' }} />
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <Link
                to="/afiliados"
                style={{
                  padding: '8px 18px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  border: '1.5px solid var(--border-soft)',
                  color: 'var(--text-strong)',
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-soft)'; e.currentTarget.style.color = 'var(--text-strong)'; }}
              >
                Cadastro
              </Link>
              {dashboard && (
                <button
                  onClick={handleLogout}
                  style={{ padding: '8px 18px', borderRadius: 100, border: 'none', background: 'var(--gold)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                >
                  Sair
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 md:px-8 py-12">
          {!dashboard ? (
            /* ── Login ── */
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 32 }}>
              <section
                className="glass w-full max-w-md rounded-3xl p-8 md:p-10"
                style={{
                  background: 'var(--surface-white)',
                  border: '1px solid var(--border-soft)',
                  boxShadow: '0 8px 40px rgba(200,131,42,0.08), 0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ marginBottom: 24 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 12px', borderRadius: 999,
                    background: 'var(--badge-bg)', color: 'var(--gold)',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    marginBottom: 14,
                  }}>
                    Área restrita
                  </span>
                  <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', color: 'var(--heading-ink)', margin: 0 }}>
                    Portal do Afiliado
                  </h1>
                  <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 8, marginBottom: 0 }}>
                    Entre com seu e-mail e cupom para ver suas vendas e pagamentos.
                  </p>
                </div>

                <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif" }}>
                    E-mail
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" style={inputStyle} />
                  </label>

                  <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif" }}>
                    Cupom
                    <input type="text" value={cupom} onChange={(e) => setCupom(e.target.value.toUpperCase())} placeholder="SEUCUPOM10" style={{ ...inputStyle, textTransform: 'uppercase' }} />
                  </label>

                  {error && (
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)', color: '#a53125', fontSize: 13, whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      marginTop: 4, width: '100%', padding: '14px 16px', borderRadius: 100, border: 'none',
                      background: loading ? 'var(--earth-light)' : 'var(--gold)',
                      color: '#fff', fontWeight: 700, fontSize: 15,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontFamily: "'DM Sans', sans-serif",
                      boxShadow: loading ? 'none' : '0 4px 20px rgba(255,145,77,0.35)',
                      transition: 'background 0.2s, box-shadow 0.2s',
                    }}
                  >
                    {loading ? 'Entrando...' : 'Entrar no portal'}
                  </button>
                </form>
              </section>
            </div>
          ) : (
            /* ── Dashboard ── */
            <section>
              {/* Welcome card */}
              <div className="glass rounded-3xl p-6 md:p-8 mb-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)' }}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 30, letterSpacing: '-0.03em', color: 'var(--heading-ink)', margin: 0 }}>
                      Olá, {dashboard.afiliado.nome}
                    </h1>
                    <p style={{ color: 'var(--text-muted)', marginTop: 6, marginBottom: 0, fontSize: 14 }}>
                      Cupom: <strong style={{ color: 'var(--amber-deep)' }}>{cuponsStr || '-'}</strong>
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(dashboard.afiliado.cupons || []).map((c) => (
                      <span key={c} style={{ padding: '6px 14px', borderRadius: 100, background: 'var(--gold-pale)', border: '1px solid var(--gold-light)', color: 'var(--amber-deep)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em' }}>
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dados de pagamento */}
              <DadosPagamentoCard
                afiliado={dashboard.afiliado}
                onEdit={() => setPixModalOpen(true)}
                onCancelPending={handleCancelPending}
                cancelLoading={cancelPendingLoading}
                cancelError={cancelPendingError}
              />

              {/* Kit de divulgação */}
              <ShareKit cupom={dashboard.afiliado.cupons?.[0] || ''} />

              {/* Stats grid */}
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Vendas com cupom', value: String(dashboard.resumo.total_vendas), accent: false },
                  { label: 'Comissão total',   value: formatCurrency(dashboard.resumo.total_comissao), accent: false },
                  { label: 'A receber',        value: formatCurrency(dashboard.resumo.total_pendente), accent: true },
                  { label: 'Total pago',       value: formatCurrency(dashboard.resumo.total_pago), accent: false },
                ].map((card) => (
                  <div
                    key={card.label}
                    className="glass rounded-2xl"
                    style={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)', padding: '18px 20px' }}
                  >
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{card.label}</p>
                    <p style={{ margin: '8px 0 0', fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: card.accent ? 'var(--gold)' : 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif" }}>{card.value}</p>
                  </div>
                ))}
              </div>

              {/* Vendas table */}
              <div className="glass rounded-2xl overflow-hidden" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)' }}>
                <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border-soft)' }}>
                  <h2 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 20, letterSpacing: '-0.02em', color: 'var(--heading-ink)' }}>
                    Vendas e pagamentos
                  </h2>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                    Lista de compras feitas com seu cupom e previsão de pagamento via PIX.
                  </p>
                </div>

                <div>
                  {(() => {
                    const realVendas = (dashboard.vendas || []).filter(
                      (v) => v && v.pedido_ref_id && String(v.pedido_ref_id).trim() !== ''
                    );
                    if (realVendas.length === 0) {
                      return <VendasEmptyState cupom={dashboard.afiliado.cupons?.[0] || ''} />;
                    }
                    return (
                    <>
                      {/* Table header */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.9fr 0.9fr', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border-fade)' }}>
                        {['Cliente', 'Comissão', 'Compra', 'Disponível / Pago em', 'Status'].map((h) => (
                          <p key={h} style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</p>
                        ))}
                      </div>
                      {realVendas.map((v) => (
                        <div
                          key={v.id}
                          style={{
                            padding: '14px 20px',
                            borderBottom: '1px solid var(--border-fade)',
                            display: 'grid',
                            gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.9fr 0.9fr',
                            gap: 12,
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <p style={{ margin: 0, color: 'var(--text-body)', fontWeight: 600, fontSize: 14 }}>{v.comprador_nome || 'Cliente'}</p>
                            <p style={{ margin: '2px 0 0', color: 'var(--text-soft)', fontSize: 12 }}>Pedido: {v.pedido_ref_id}</p>
                          </div>
                          <p style={{ margin: 0, fontSize: 14, color: 'var(--heading-ink)', fontWeight: 700 }}>{formatCurrency(v.valor_comissao)}</p>
                          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{formatDateTime(v.compra_em)}</p>
                          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                            {v.status === 'pago' ? formatDateTime(v.pago_em) : formatDateTime(v.disponivel_em)}
                          </p>
                          <div><StatusBadge status={v.status} /></div>
                        </div>
                      ))}
                    </>
                    );
                  })()}
                </div>
              </div>
            </section>
          )}
        </main>

        {dashboard && (
          <EditarPixModal
            open={pixModalOpen}
            onClose={() => setPixModalOpen(false)}
            token={localStorage.getItem(AFILIADO_TOKEN_KEY) || ''}
            whatsappFallbackMascarado={maskWhatsApp(dashboard.afiliado.whatsapp)}
            onSuccess={handlePixModalSuccess}
          />
        )}
      </div>
    </>
  );
}
